import { Suspense, useEffect } from 'react';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';

import talintyLogo from '@/assets/images/Talinty.png';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { useBrandingStore } from '@/stores/useBrandingStore';
import { FullPageLoader } from '@/components/ui/FullPageLoader';
import { useAssessment } from '@/features/assessment/hooks/useAssessment';
import { useAssessmentProgress } from '@/features/assessment/hooks/useAssessmentProgress';
import { useAssessmentStore } from '@/features/assessment/stores/useAssessmentStore';

import { LanguageSwitcher } from './LanguageSwitcher';

function TalintyLogo() {
  const brandLogo = useBrandingStore((s) => s.logo);

  return (
    <Box
      component="img"
      src={brandLogo ?? talintyLogo}
      alt="Talinty"
      sx={{ height: 34, objectFit: 'contain' }}
      loading="lazy"
    />
  );
}

// ─── Shared chrome: green canvas + 1440 column + navbar ──────────────────────
function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box
        sx={{
          maxWidth: 1440,
          mx: 'auto',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          borderInlineStart: '1px solid',
          borderInlineEnd: '1px solid',
          borderColor: 'divider',
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'background.default',
            boxShadow: 'none',
            color: 'text.primary',
            pt: 1,
            px: { xs: 1, sm: 2, md: 6, lg: 15 },
            pb: 0,
            borderBottom: 'none',
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '20px',
              border: '1px solid',
              borderColor: 'divider',
              px: { xs: 2, sm: 2.5 },
              minHeight: '55px !important',
              justifyContent: 'space-between',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <TalintyLogo />
            <LanguageSwitcher />
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 1, sm: 2, md: 6, lg: 15 },
            pt: 2,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Static shell — error / info pages outside the assessment flow ────────────
export function StaticShell() {
  return (
    <PageShell>
      <Outlet />
    </PageShell>
  );
}

// ─── Assessment data guard ────────────────────────────────────────────────────
function AssessmentLoader({ children }: { children: React.ReactNode }) {
  const { token } = useParams<{ token: string }>();
  const setToken = useAssessmentStore((s) => s.setToken);
  const storeToken = useAssessmentStore((s) => s.token);

  useEffect(() => {
    if (token && storeToken !== token) {
      setToken(token);
    }
  }, [token, storeToken, setToken]);

  const setCheatingTries = useAssessmentStore((s) => s.setCheatingTries);
  const setStarted = useAssessmentStore((s) => s.setStarted);

  // Both queries run in parallel — Suspense in AssessmentShell holds the loader
  // until both resolve, ensuring the store has the correct current question index
  // before any child (including AssessmentPage) renders.
  const { data } = useAssessment(token ?? '');
  const { data: progressData } = useAssessmentProgress(token ?? '');

  const { cheatingTries, startDate, plannedEndDate, status: stepStatus } = data ?? {};
  // Prefer the progress endpoint's evaluation for all routing decisions.
  // useAssessmentProgress has staleTime:0 and refetchOnWindowFocus:true, so it
  // picks up lock / disqualification changes made by an admin while the candidate
  // is mid-exam — on the next window-focus refetch the shell redirects immediately
  // instead of waiting for the next write API call to return 423.
  // Fall back to the static data for the initial render (both resolve together via
  // Suspense, so this fallback is only a type-safety net).
  const evaluation = progressData?.evaluation ?? data?.evaluation;
  const lockReason = evaluation?.lockInfo?.reason ?? null;

  const location = useLocation();
  // Paths where we must NOT redirect even if evaluation is INPROGRESS
  const isOnExamFlow =
    location.pathname.endsWith('/exam') ||
    location.pathname.endsWith('/feedback') ||
    location.pathname.endsWith('/complete');

  // Sync timing + anti-cheat state from the static load so the store is
  // always accurate after a hard reload (before progress fetch fires).
  useEffect(() => {
    if (startDate && plannedEndDate) {
      setStarted(new Date(startDate).getTime(), new Date(plannedEndDate).getTime());
    }
    if (cheatingTries != null) {
      setCheatingTries(cheatingTries);
    }
  }, [startDate, plannedEndDate, cheatingTries, setStarted, setCheatingTries]);

  // evaluation.status FINISHED — evaluation pipeline marked the assessment done
  if (evaluation?.status === 'FINISHED') {
    if (location.pathname.endsWith('/complete')) {
      return <>{children}</>;
    }
    return <Navigate to={`/${token}/complete`} replace />;
  }

  // evaluation.status LOCKED — hard block, nothing further is possible.
  if (evaluation?.status === 'LOCKED') {
    let reason: string;
    if (lockReason === 'timeout') {
      reason = 'timeout';
    } else if (lockReason && lockReason !== 'timeout') {
      reason = 'cheating';
    } else {
      reason = (cheatingTries ?? 0) >= 3 ? 'cheating' : 'timeout';
    }
    return <Navigate to={`/${token}/unavailable`} state={{ reason }} replace />;
  }

  // Step status FINISHED — answerQuestion closed the step as soon as all
  // questions were answered. If feedback is enabled and the candidate hasn't
  // submitted it yet, route to /feedback first; otherwise go to /complete.
  // NOTE: this block intentionally runs before the isDisqualified check below.
  // Disqualification is determined at the end of answerQuestion (same moment
  // the step closes), so a disqualified candidate must still be able to reach
  // the feedback page and submit. The /unavailable redirect happens after
  // feedback is given (or if feedback is not enabled / already submitted).
  if (stepStatus === 'FINISHED') {
    if (progressData.progress !== null) {
      const { stepEvaluation: progStep } = progressData;
      if (progStep.enableFeedback && !progressData.progress.feedback) {
        if (location.pathname.endsWith('/feedback')) {
          return <>{children}</>;
        }
        return <Navigate to={`/${token}/feedback`} replace />;
      }
    }
    // Feedback not required or already submitted — now enforce disqualification.
    if (evaluation?.isDisqualified) {
      const reason = (cheatingTries ?? 0) >= 3 ? 'cheating' : 'disqualified';
      return <Navigate to={`/${token}/unavailable`} state={{ reason }} replace />;
    }
    if (location.pathname.endsWith('/complete') || location.pathname.endsWith('/feedback')) {
      return <>{children}</>;
    }
    return <Navigate to={`/${token}/complete`} replace />;
  }

  // isDisqualified outside a finished step — admin action before the candidate
  // even started or completed the assessment.
  if (evaluation?.isDisqualified) {
    const reason = (cheatingTries ?? 0) >= 3 ? 'cheating' : 'disqualified';
    return <Navigate to={`/${token}/unavailable`} state={{ reason }} replace />;
  }

  // Assessment is in progress AND the step has actually been started (startDate is set)
  // — skip onboarding and jump straight to the exam.
  // If startDate is null the candidate never clicked "Start": show the welcome page
  // so they can begin. Without this guard, endsAt stays null in the store and
  // AssessmentTimer falls back to Date.now(), firing onExpiry immediately and
  // locking the assessment before the candidate even sees it.
  if (evaluation?.status === 'INPROGRESS' && startDate && !isOnExamFlow) {
    return <Navigate to={`/${token}/exam`} replace />;
  }

  return <>{children}</>;
}

// ─── Assessment shell — full flow with data guard ─────────────────────────────
export function AssessmentShell() {
  return (
    <PageShell>
      <ErrorBoundary>
        <Suspense fallback={<FullPageLoader />}>
          <AssessmentLoader>
            <Outlet />
          </AssessmentLoader>
        </Suspense>
      </ErrorBoundary>
    </PageShell>
  );
}
